import { Flex, useToast } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { LocationCredentialsTable } from './LocationCredentialsTable';
import { useGetLocationCredentials, useSaveLocationCredentials } from '../../hooks/useLocationCredentials';
import { usePullOpsTraxLocations } from '../../hooks/usePullOpsTraxLocations';
import { AgenticStepLoader } from '../AgenticStepLoader';

interface OpsTraxLoginsStepProps {
	organizationId: string;
	locations: Array<{ _id: string; title: string }>;
	onComplete: () => void;
	onSkip: () => void;
	onBack?: () => void;
	isUploadOpen?: boolean;
	onCloseUpload?: () => void;
}

export const OpsTraxLoginsStep = ({
	organizationId,
	locations,
	onComplete,
	onSkip,
	onBack,
	isUploadOpen,
	onCloseUpload,
}: OpsTraxLoginsStepProps) => {
	const toast = useToast();
	const [opsProgressStep, setOpsProgressStep] = useState(0);
	const saveCredentials = useSaveLocationCredentials();
	const pullOps = usePullOpsTraxLocations();
	const existingCredentials = useGetLocationCredentials(organizationId, 'opstrax');
	const isOpsRunning = pullOps.submit.isPending || pullOps.isPolling;

	const opsAgenticSteps = [
		{ label: 'Saving credentials', description: 'Storing your OPS login securely' },
		{ label: 'Connecting to OPS', description: 'Establishing a secure session' },
		{ label: 'Pulling location data', description: 'Retrieving locations from your account' },
		{ label: 'Attaching to account', description: 'Linking locations to your organization' },
	];

	useEffect(() => {
		if (!isOpsRunning) {
			setOpsProgressStep(0);
			return;
		}
		const interval = setInterval(() => {
			setOpsProgressStep((prev) => Math.min(prev + 1, opsAgenticSteps.length - 1));
		}, 15000);
		return () => clearInterval(interval);
	}, [isOpsRunning, opsAgenticSteps.length]);

	useEffect(() => {
		if (pullOps.isComplete) {
			toast({
				title: 'OPS pull complete',
				status: 'success',
				duration: 3000,
				isClosable: true,
			});
			onComplete();
		}
		if (pullOps.isFailed) {
			toast({
				title: 'OPS pull failed',
				description: pullOps.error ?? 'Could not pull OPS locations.',
				status: 'error',
				duration: 4000,
				isClosable: true,
			});
		}
	}, [pullOps.isComplete, pullOps.isFailed, pullOps.error, onComplete, toast]);

	const initialUsernames = Object.fromEntries(
		(existingCredentials.data ?? []).map((item) => [item.locationId, item.username])
	);

	if (isOpsRunning) {
		return (
			<Flex align="center" justify="center" width="100%" minH="400px">
				<AgenticStepLoader
					title="Pulling OPS Data"
					subtitle="Please do not close or refresh this page. This may take several minutes."
					steps={opsAgenticSteps}
					currentStep={opsProgressStep}
				/>
			</Flex>
		);
	}

	return (
		<Flex direction="column" gap={4} flex={1} minH={0}>
			<LocationCredentialsTable
				locations={locations}
				provider="opstrax"
				initialUsernames={initialUsernames}
				isLoading={saveCredentials.isPending}
				onSkip={onSkip}
				onBack={onBack}
				isUploadOpen={isUploadOpen}
				onCloseUpload={onCloseUpload}
				onSave={async (credentials) => {
					if (credentials.length === 0) {
						onSkip();
						return;
					}
					await saveCredentials.mutateAsync({
						organizationId,
						provider: 'opstrax',
						credentials,
					});
					await pullOps.submit.mutateAsync({ organizationId });
				}}
			/>
		</Flex>
	);
};
